import { memo, type Key } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Autocomplete } from '@ds';
import { AddressSearch, AdminFormRow, ConditionalRender } from '@ui';
import { renderAutocompleteItem } from '@/utils/helpers';
import { ControlledTextInput } from './inputs/ControlledTextInput';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useAddressForm } from '@/hooks/useAddressForm';
import { useCountrySubdivisionInfo } from '@/hooks/useCountrySubdivisionInfo';
import { LocationFormType } from '@/types';
import { RegionSelect } from './RegionSelect';

type FacilityLocationDetailsProps = {
  isReadOnly?: boolean;
  isRequired?: boolean;
  showRegion?: boolean;
};

export const FacilityLocationDetails = memo(
  ({ isReadOnly, isRequired = true, showRegion }: FacilityLocationDetailsProps) => {
    const {
      control,
      formState: { touchedFields },
    } = useFormContext<LocationFormType>();

    const country = useWatch({ control, name: 'country' });
    const state = useWatch({ control, name: 'state' });

    const { countriesSelectList } = useCountriesData();

    const { countryHasStates, stateLabel } = useCountrySubdivisionInfo(country);

    const {
      addressValue,
      addressList,
      stateSelectList,
      onAddressSelect,
      onStateInputChange,
      onAddressInputChange,
      onCountryInputChange,
      onRegionSelectChange,
    } = useAddressForm();

    const stateNotSelected = countryHasStates && !state;
    const addressDisabled = !country || stateNotSelected;

    return (
      <>
        <AdminFormRow title="Country" isRequired={isRequired}>
          <Controller
            name="country"
            control={control}
            render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <Autocomplete
                placeholder="Choose an option"
                aria-label="Country"
                isDisabled={isReadOnly}
                defaultItems={countriesSelectList}
                isInvalid={invalid}
                errorMessage={error?.message}
                selectedKey={value || null}
                onSelectionChange={(key: Key | null) => {
                  onChange(key);
                  onCountryInputChange(key as string);
                }}
                {...field}
              >
                {renderAutocompleteItem}
              </Autocomplete>
            )}
          />
        </AdminFormRow>
        {showRegion && <RegionSelect isReadOnly={isReadOnly} onRegionSelectChange={onRegionSelectChange} />}
        <ConditionalRender show={countryHasStates}>
          <AdminFormRow title={stateLabel} isRequired={isRequired}>
            <Controller
              name="state"
              control={control}
              render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
                <Autocomplete
                  placeholder="Choose an option"
                  aria-label="State"
                  isDisabled={isReadOnly}
                  defaultItems={stateSelectList}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  selectedKey={value || null}
                  onSelectionChange={(key: Key | null) => {
                    onChange(key);
                    onStateInputChange(key as string);
                  }}
                  {...field}
                >
                  {renderAutocompleteItem}
                </Autocomplete>
              )}
            />
          </AdminFormRow>
        </ConditionalRender>
        <AdminFormRow title="Address" isRequired={isRequired}>
          <Controller
            name="addressLine"
            control={control}
            render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <AddressSearch
                addressList={addressList}
                onAddressSelect={(addressText) => onAddressSelect(addressText, true)}
                onAddressInputChange={onAddressInputChange}
                value={addressValue}
                onChange={onChange}
                isInvalid={Boolean(invalid && touchedFields.addressLine)}
                isDisabled={isReadOnly || addressDisabled}
                errorMessage={touchedFields.addressLine ? error?.message : ''}
                wrapperProps={{ 'aria-label': 'Search Address' }}
                {...field}
              />
            )}
          />
        </AdminFormRow>
        <AdminFormRow title="City" isRequired={isRequired}>
          <ControlledTextInput name="city" aria-label="City" isReadOnly isDisabled />
        </AdminFormRow>
        <AdminFormRow title="Postal Code" isRequired={isRequired}>
          <ControlledTextInput name="postalCode" aria-label="Postal Code" isReadOnly isDisabled />
        </AdminFormRow>
        <AdminFormRow title="Timezone" isRequired={isRequired}>
          <ControlledTextInput name="timezone" aria-label="Timezone" isReadOnly isDisabled />
        </AdminFormRow>
      </>
    );
  },
);
